import { useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "./nl";
import { entityTrack } from "@/lib/entities";

/* Related Entities — deterministic registry relationships only (Figma contract).
   Official marks from the Entity Asset Registry; initials fallback when a
   verified asset does not exist. STRICT navigation truth (Rule 6): live
   entities route to their canonical page; non-live entities render as
   informational identity cards with NO click affordance. */

interface Card {
  id: string; type: string; name: string; accent: string;
  relation: string; relationShort: string; route: string | null; logo?: string | null;
}

const PROTO_ACCENTS = ["#1fe09c", "#3d87ff", "#7a3dff"];

const Mark = ({ name, accent, logo }: { name: string; accent: string; logo?: string | null }) => {
  const [err, setErr] = useState(false);
  return (
    <span className="grid place-items-center rounded-full shrink-0 w-[36px] h-[36px] lg:w-[52px] lg:h-[52px] bg-[#0a0b12]"
      style={{ border: `1.5px solid ${accent}` }} aria-hidden>
      {logo && !err ? (
        <img src={`${API_BASE}${logo}`} alt="" loading="lazy" decoding="async"
          className="w-[22px] h-[22px] lg:w-[32px] lg:h-[32px] rounded-full" onError={() => setErr(true)} />
      ) : (
        <span className="nl-sg text-[14px] lg:text-[20px] font-medium" style={{ color: accent }}>{(name || "?")[0]}</span>
      )}
    </span>
  );
};

export const RelatedEntities = ({ overview }: { overview: any }) => {
  const rel = overview.related;
  if (!rel) return null;
  const name = overview.identity.name;
  const category = (overview.identity.category || "protocol").toUpperCase();
  const cards: Card[] = [];
  if (rel.token)
    cards.push({ id: rel.token.id, type: "TOKEN", name: rel.token.symbol || rel.token.name, accent: "#7a3dff",
      relation: "ASSOCIATED TOKEN", relationShort: "ASSOCIATED",
      route: rel.token.live && rel.token.route ? rel.token.route : null,
      logo: rel.token.logo });
  const chain = (rel.chains || [])[0];
  if (chain)
    cards.push({ id: chain.id, type: "CHAIN", name: (chain.name || "").replace(/ Network$/, ""), accent: "#05d1f0",
      relation: "DEPLOYED ON", relationShort: "DEPLOYED ON",
      route: chain.live && chain.route ? chain.route : null,
      logo: chain.logo });
  (rel.protocols || []).slice(0, 3).forEach((p: any, i: number) =>
    cards.push({ id: p.id, type: "PROTOCOL", name: p.name, accent: PROTO_ACCENTS[i % 3],
      relation: `RELATED ${category}`, relationShort: "RELATED",
      route: p.live && p.route ? p.route : null,
      logo: p.logo }));
  if (!cards.length) return null;
  return (
    <section data-testid="related-entities">
      <p className="nl-geist text-[9px] md:text-[10px] lg:text-[11px] leading-[14px] font-semibold text-[#05d1f0] tracking-[0.02em]">ENTITY GRAPH</p>
      <h2 className="nl-sg text-[27px] leading-[35px] md:text-[31px] md:leading-[40px] lg:text-[34px] lg:leading-[43px] font-medium text-[#f6f7fc] mt-[8px]">
        Related Entities
      </h2>
      <p className="nl-geist hidden lg:block text-[15px] leading-[19px] text-[#9499ad] mt-[6px]">
        Deterministic relationships create navigation through the onchain economy.
      </p>
      <p className="nl-geist lg:hidden text-[12px] md:text-[14px] leading-[16px] md:leading-[18px] text-[#9499ad] mt-[6px]">
        Deterministic relationships, not generic recommendations.
      </p>
      <div className="mt-[22px] lg:rounded-[26px] lg:bg-[#030405] lg:border lg:border-[#171a26] lg:p-[24px]">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[10px] lg:gap-[24px]">
          {cards.map((c) => {
            const inner = (
              <>
                <Mark name={c.name} accent={c.accent} logo={c.logo} />
                <p className="nl-mono text-[7px] lg:text-[9px] leading-[10px] lg:leading-[12px] font-medium text-[color:var(--nl-text-faint)] mt-[14px] lg:mt-[18px] uppercase">{c.type}</p>
                <p className="nl-sg text-[15px] leading-[19px] lg:text-[20px] lg:leading-[26px] font-medium text-[#f6f7fc] mt-[6px] truncate">{c.name}</p>
                <p className="nl-geist text-[8px] lg:text-[10px] leading-[10px] lg:leading-[13px] font-semibold mt-[8px] lg:mt-[10px]"
                  style={{ color: c.accent }}>
                  <span className="lg:hidden">{c.relationShort}</span>
                  <span className="hidden lg:inline">{c.relation}</span>
                </p>
              </>
            );
            if (!c.route) {
              return (
                <div key={c.id} data-testid={`related-card-${c.id.replace(":", "-")}`} data-live="false"
                  className="rounded-[16px] lg:rounded-[22px] bg-[#060609] border border-[#141724] p-[12px] lg:p-[18px] flex flex-col">
                  {inner}
                </div>
              );
            }
            return (
              <Link key={c.id} to={c.route} data-live="true"
                onClick={() => entityTrack("related_entity_click", overview.identity.entity_id, { target: c.id })}
                className="group rounded-[16px] lg:rounded-[22px] bg-[#060609] border border-[#141724] p-[12px] lg:p-[18px] hover:border-[#262a40] transition-colors duration-200 flex flex-col focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#05d1f0]/60"
                data-testid={`related-card-${c.id.replace(":", "-")}`}>
                {inner}
                <p className="nl-geist text-[8px] lg:text-[11px] leading-[10px] lg:leading-[14px] font-medium text-[#9499ad] mt-auto pt-[16px] lg:pt-[26px] group-hover:text-white transition-colors duration-200">
                  View entity →
                </p>
              </Link>
            );
          })}
        </div>
        <p className="nl-geist text-[9px] lg:text-[11px] leading-[14px] text-[color:var(--nl-text-faint)] mt-[16px] lg:mt-[20px]"
          data-testid="related-provenance">
          Relationships from the NewsLayer Entity Graph
        </p>
      </div>
    </section>
  );
};
